import { useGetWaitlistFeatureFlagDetails, useQuickFillFeatureDetails } from '@frontend/api-quick-fill';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { useCalendarEventsConfigurationShallowStore } from '@frontend/schedule-helpers';
import { dispatchTopBarTraySelectionEvent } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Modal, ModalControlModalProps, RadioField, useFormField } from '@frontend/design-system';
import { CALENDAR_TRACKING_IDS } from '../../../trackingIds/calendar-tracking-ids';
import { CalendarEventsEnums } from '../types';

const trackingIds = CALENDAR_TRACKING_IDS.eventTypeSelectionModal;

type CalendarEventSelectorProps = {
  modalProps: ModalControlModalProps;
  onCalendarEventSelect: (event: CalendarEventsEnums) => void;
  onModalClose: () => void;
};

export const CalendarEventSelector = ({
  modalProps,
  onCalendarEventSelect,
  onModalClose,
}: CalendarEventSelectorProps) => {
  const { t } = useTranslation('scheduleCalendarEvents');
  const { isWaitlistDirectBookingEnabled } = useGetWaitlistFeatureFlagDetails();
  const { locationIdWiseQuickFillFeature } = useQuickFillFeatureDetails();
  const { configuredCalendarEvent } = useCalendarEventsConfigurationShallowStore('configuredCalendarEvent');

  const calendarEventsSelectorRadioFields = useFormField({
    type: 'radio',
    value: CalendarEventsEnums.APPOINTMENT_EVENT,
  });

  const handleCalendarEventSelection = () => {
    if (calendarEventsSelectorRadioFields.value === CalendarEventsEnums.WAITLIST_MESSAGE_EVENT) {
      dispatchTopBarTraySelectionEvent('quickfill');
      SchedulerV3.triggerWaitlistCustomEvent('schedule:waitlist:set-context', {
        locationId: configuredCalendarEvent?.locationId,
        providerId: configuredCalendarEvent?.providerId,
        selectedDate: configuredCalendarEvent?.calendarDateValue,
        selectedTime: configuredCalendarEvent?.startHour,
      });
      modalProps.onClose();

      return;
    }

    const event =
      calendarEventsSelectorRadioFields?.value === CalendarEventsEnums.APPOINTMENT_EVENT
        ? CalendarEventsEnums.APPOINTMENT_EVENT
        : CalendarEventsEnums.PROVIDER_OUT_OF_OFFICE_EVENT;

    modalProps.onClose();
    onCalendarEventSelect(event);
  };

  const isWaitlistEnabled = locationIdWiseQuickFillFeature[configuredCalendarEvent?.locationId ?? ''];

  return (
    <Modal {...modalProps} onClose={onModalClose}>
      <Modal.Header>{t('Select Calendar Event')}</Modal.Header>
      <Modal.Body css={{ marginTop: theme.spacing(2) }}>
        <RadioField {...calendarEventsSelectorRadioFields} name='event-type'>
          <RadioField.Radio
            trackingId={trackingIds.appointmentRadioButton}
            value={CalendarEventsEnums.APPOINTMENT_EVENT}
          >
            {t('New Appointment')}
          </RadioField.Radio>
          <RadioField.Radio
            trackingId={trackingIds.outOfOfficeEventRadioButton}
            value={CalendarEventsEnums.PROVIDER_OUT_OF_OFFICE_EVENT}
          >
            {t('New Out of Office Event')}
          </RadioField.Radio>

          {isWaitlistEnabled ? (
            <RadioField.Radio
              trackingId={trackingIds.waitlistMessageRadioButton}
              value={CalendarEventsEnums.WAITLIST_MESSAGE_EVENT}
            >
              {isWaitlistDirectBookingEnabled ? t('Send Waitlist Booking Link') : t('Send Waitlist Message')}
            </RadioField.Radio>
          ) : null}
        </RadioField>
      </Modal.Body>
      <Modal.Footer
        primary={{
          label: t('Select'),
          onClick: handleCalendarEventSelection,
          trackingId: trackingIds.selectButton,
        }}
        secondary={{
          label: t('Cancel'),
          onClick: onModalClose,
          trackingId: trackingIds.cancelButton,
        }}
      />
    </Modal>
  );
};
